import { useMemo, useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Paragraph, Flex, Heading, Button, Spinner, Box } from 'theme-ui';
import {
  useCartRemoveItem,
  useCartDiscounts,
  useSettings,
} from '@backpackjs/storefront';

import store, { useRecoilValue } from '@store';
import { Svg, Picture, Locale, Link } from '@snippets';
import { useSidebar, useCartStatus } from '@hooks';

import { useByobLineItem } from './useByobLineItem';
import { Price } from './Price';
import { themed } from './LineItem.theme';

export const LoyaltyLionItem = themed(
  ({
    theme,
    inSidebar,
    item,
    isLast,
    cart,
    isMaxed,
    qtyInCart,
    maxQty,
    chargeItems,
    ...props
  }) => {
    const settings = useSettings();
    const cartDiscounts = useCartDiscounts();
    const { cartRemoveItem, ...removeStatus } = useCartRemoveItem({
      autoReset: 100,
    });
    const [, { setUpdatingCartStatus }] = useCartStatus();
    const [, { closeSidebar }] = useSidebar();
    const { isByob, byobMessage } = useByobLineItem({
      item,
      updatedAt: cart?.updatedAt,
      discountCodeApplied: cartDiscounts?.length,
    });
    const mysteryItems = (settings?.mystery?.items || '').split(',');

    const gwpVariantIds = useRecoilValue(store.gwpVariantIds);
    const [isDeleting, setIsDeleting] = useState(false);

    const { handle } = item.variant.product;
    const isGift =
      gwpVariantIds?.includes(item.variant.id) || (handle && mysteryItems?.includes(handle));
    const { started, finished, success, errors } = removeStatus;
    const ignoredProducts = settings?.cart?.bulkOrdering?.ignoredProducts;
    const { availableForSale } = { ...item.variant };

    const productUrl = null;
    const fullTitle = item.variant?.product?.title;
    const image = item.variant?.image;

    const sizeVariant = item.variant?.selectedOptions?.find(
      (option) => option.name === 'Size'
    );
    const colorVariant = item.variant?.selectedOptions?.find(
      (option) => option.name === 'Color'
    );

    const title = useMemo(() => {
      const splitIndex = fullTitle?.indexOf('|') || -1;
      return {
        product:
          splitIndex >= 0 ? fullTitle?.slice(0, splitIndex).trim() : fullTitle,
        variant: splitIndex >= 0 ? fullTitle?.slice(splitIndex + 1).trim() : '',
      };
    }, [fullTitle]);

    const handleRemove = useCallback(async () => {
      setIsDeleting(true);
      await cartRemoveItem({ lineId: item.id });
      setIsDeleting(false);
    }, [item.id]);

    const discountDescription =
      item.discountAllocations?.[0]?.discountApplication?.description;

    const setGlobalCartStatusOnUpdating = useCallback(() => {
      setUpdatingCartStatus({ started, finished, success, errors });
      return () => {
        setUpdatingCartStatus({
          started: false,
          finished: false,
          success: false,
          errors: [],
        });
      };
    }, [started, finished, success]);

    const removeItemFromCartIfUnavailable = useCallback(() => {
      if (availableForSale) return;
      cartRemoveItem({ lineId: item.id });
    }, [availableForSale, item.id]);

    useEffect(() => {
      setGlobalCartStatusOnUpdating();
    }, [started, finished, success]);
    useEffect(() => {
      removeItemFromCartIfUnavailable();
    }, [availableForSale]);

    return (
      <Flex
        data-comp={LoyaltyLionItem.displayName}
        {...props}
        sx={{
          ...theme.wrapper,
          ...props.sx,
          borderBottomStyle: isLast ? 'none' : 'solid',
        }}
      >
        {/* Image */}
        <Link href={productUrl} tabIndex="-1" aria-label={title.product}>
          <Picture
            alt={title.product}
            images={[
              {
                src: image?.src,
                ratio: 1,
                width: 105,
              },
            ]}
            sx={theme.image}
          />
        </Link>

        <Flex sx={theme.details}>
          <Flex sx={theme.top}>
            <Flex sx={theme.titleWrapper}>
              {/* Product title */}
              <Link href={productUrl}>
                <Heading as="h5" sx={theme.title}>
                  {title.product}
                </Heading>
              </Link>

              {/* Variant title */}
              {sizeVariant?.value || colorVariant?.value ? (
                <Paragraph sx={theme.variantTitle}>
                  {sizeVariant?.value || colorVariant?.value}
                </Paragraph>
              ) : (
                <Paragraph sx={theme.variantTitle}>{title.variant}</Paragraph>
              )}

              {isGift &&
                parseInt(item.estimatedCost.subtotalAmount.amount, 10) ===
                  0 && (
                  <Paragraph
                    sx={{ ...theme.variantTitle, color: isGift ? 'red' : '' }}
                  >
                    Free Gift With Purchase
                  </Paragraph>
                )}

              {isByob && (
                <Paragraph sx={theme.variantTitle}>
                  With Personalization
                </Paragraph>
              )}

              {discountDescription && !byobMessage && (
                <Paragraph sx={{ ...theme.variantTitle, color: 'red' }}>
                  {discountDescription}
                </Paragraph>
              )}

              {byobMessage && (
                <Paragraph sx={{ ...theme.variantTitle, color: 'red' }}>
                  {byobMessage}
                </Paragraph>
              )}
            </Flex>

            {/* Remove button */}
            {handle !== 'byob-charge' && (
              <Button
                variant="buttons.plain"
                onClick={handleRemove}
                sx={theme.closeButton}
                aria-label="Remove item from cart"
              >
                <Svg
                  alt="Close"
                  src="/svgs/close.svg#close"
                  viewBox="0 0 48 48"
                  sx={theme.closeIcon}
                />
              </Button>
            )}
          </Flex>

          <Box sx={{ width: '100%' }}>
            <Flex sx={theme.bottom}>
              <Box />

              {/* Price */}
              <Locale.CartLine line={item}>
                <Locale.Price>
                  {(props) => <Price {...props} inSidebar={inSidebar} />}
                </Locale.Price>
              </Locale.CartLine>
            </Flex>
          </Box>
        </Flex>

        {isDeleting && (
          <Flex sx={theme.deleting}>
            <Spinner sx={theme.deleting.spinner} />
          </Flex>
        )}
      </Flex>
    );
  }
);

LoyaltyLionItem.displayName = 'LoyaltyLionItem';
LoyaltyLionItem.propTypes = {
  item: PropTypes.object,
  index: PropTypes.number,
  inSidebar: PropTypes.bool,
};
